// src/screens/QuestionnaireScreen.js - Dynamic questions from eventQuestions collection
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import {
  subscribeQuestions,
  peekQuestions,
} from "../services/questionsCache";
import { getQRData } from "../services/localStorageService";
import { requestReward } from "../services/voucherService";
import { toast } from "react-toastify";

/**
 * An answer slot per question, keeping anything already typed.
 *
 * Keyed by question id so a live edit in the portal cannot wipe an answer the
 * guest has already given. Multi-select starts as an array because its handler
 * pushes into one.
 */
function blankAnswers(rows, prev = {}) {
  const next = {};
  rows.forEach((q) => {
    next[q.id] =
      prev[q.id] !== undefined
        ? prev[q.id]
        : q.questionType === "multi_select"
        ? []
        : "";
  });
  return next;
}

const QuestionnaireScreen = ({ qrData, userData }) => {
  const navigate = useNavigate();
  const eventId = (qrData || getQRData())?.eventId || null;

  // Already fetched, in almost every real visit. The listener was primed at the
  // scan, three screens ago, so this is the answer rather than a promise of one
  // and the first paint has the questions on it. `null` only when a guest
  // deep-links straight here, or on a connection slow enough that three screens
  // went by without a round trip completing.
  const prefetched = peekQuestions(eventId);

  const [loading, setLoading] = useState(prefetched === null);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState(prefetched || []);
  const [answers, setAnswers] = useState(() => blankAnswers(prefetched || []));
  const [noQuestions, setNoQuestions] = useState(false);
  // Once a guest has been shown questions, an empty update must not shunt them
  // to the thank-you screen mid-answer.
  const hadQuestionsRef = useRef((prefetched || []).length > 0);

  // Live, not a one-shot read.
  //
  // The subscription lives in services/questionsCache.js and was started when
  // the code was scanned, so this attaches to a listener that already has an
  // answer. It stays live either way, so an edit made in the portal, a new
  // option or a reworded question, still appears on the phone as it is saved.
  // Answers already given are preserved across an update, keyed by question id.
  useEffect(() => {
    if (!eventId) {
      toast.error("Activation data not found");
      navigate("/scan");
      return undefined;
    }

    const unsubscribe = subscribeQuestions(eventId, (rows, failed) => {
      if (failed) {
        setLoading(false);
        // Never strand the guest: carry on to the reward step.
        if (!hadQuestionsRef.current) setNoQuestions(true);
        return;
      }
      if (!rows) return;

      if (rows.length === 0) {
        if (!hadQuestionsRef.current) setNoQuestions(true);
        setLoading(false);
        return;
      }

      hadQuestionsRef.current = true;
      setQuestions(rows);
      setAnswers((prev) => blankAnswers(rows, prev));
      setLoading(false);
    });

    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle the no-questions case in useEffect (NOT during render)
  useEffect(() => {
    if (noQuestions && !submitting) {
      handleNoQuestionsFlow();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noQuestions]);

  const handleNoQuestionsFlow = async () => {
    setSubmitting(true);
    try {
      const promotionData = qrData || getQRData();
      if (!promotionData) {
        navigate("/scan");
        return;
      }

      // No questions to save - go straight to voucher claim
      let claimSent = false;
      let limitReached = false;
      try {
        // One server call: it checks the scan limit, the global email cap and
        // whether any codes are left, creates the claim and sends the email.
        const outcome = await requestReward({
          eventId: promotionData.eventId,
          customerId: userData.uid,
          customerEmail: userData.email,
          customerName: `${userData.firstName || ""} ${userData.lastName || ""}`.trim(),
          bartenderId: promotionData.bartenderId,
        });
        claimSent = outcome.status === "sent";
        limitReached = outcome.status === "limit" || outcome.status === "cap";
      } catch (voucherError) {
        console.error("Voucher claim error:", voucherError);
      }

      navigate("/thank-you", { state: { claimSent, limitReached } });
    } catch (error) {
      console.error("Error in no-questions flow:", error);
      navigate("/thank-you", { state: { claimSent: false } });
    }
  };

  const handleAnswerChange = (questionId, value, questionType) => {
    if (questionType === "multi_select") {
      setAnswers((prev) => {
        const current = prev[questionId] || [];
        const updated = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value];
        return { ...prev, [questionId]: updated };
      });
    } else {
      setAnswers((prev) => ({ ...prev, [questionId]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const promotionData = qrData || getQRData();
      if (!promotionData) {
        toast.error("Promotion data not found");
        navigate("/scan");
        return;
      }

      // Build answers array in the new format
      const answersArray = questions.map((q) => ({
        questionId: q.id,
        questionText: q.questionText,
        questionType: q.questionType,
        answer:
          q.questionType === "multi_select"
            ? (answers[q.id] || []).join(", ")
            : answers[q.id] || "",
      }));

      // Save responses to Firestore
      await addDoc(collection(db, "customerResponses"), {
        answers: answersArray,
        eventId: promotionData.eventId,
        eventName: promotionData.eventName,
        bartenderId: promotionData.bartenderId,
        bartenderName: promotionData.bartenderName,
        userId: userData.uid,
        userName: `${userData.firstName || ""} ${userData.lastName || ""}`.trim(),
        userEmail: userData.email,
        timestamp: serverTimestamp(),
      });

      // Attempt voucher claim (voucher assigned only when email link is clicked)
      let claimSent = false;
      let limitReached = false;
      try {
        // One server call: it checks the scan limit, the global email cap and
        // whether any codes are left, creates the claim and sends the email.
        const outcome = await requestReward({
          eventId: promotionData.eventId,
          customerId: userData.uid,
          customerEmail: userData.email,
          customerName: `${userData.firstName || ""} ${userData.lastName || ""}`.trim(),
          bartenderId: promotionData.bartenderId,
        });
        claimSent = outcome.status === "sent";
        limitReached = outcome.status === "limit" || outcome.status === "cap";
      } catch (voucherError) {
        console.error("Voucher assignment error:", voucherError);
        // Don't block the flow - voucher is optional
      }

      // Navigate to thank you - voucher will come via email
      navigate("/thank-you", { state: { claimSent, limitReached } });
    } catch (error) {
      console.error("Error submitting questionnaire:", error);
      toast.error("Error submitting responses. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading questions...</p>
      </div>
    );
  }

  if (noQuestions || questions.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Processing...</p>
      </div>
    );
  }

  return (
    <div className="screen-container">
      <h1 className="screen-title">Share Your Experience</h1>

      <form onSubmit={handleSubmit}>
        {questions.map((question) => (
          <div key={question.id} className="form-group">
            <label>{question.questionText}</label>

            {/* Multiple Choice */}
            {question.questionType === "multiple_choice" && (
              <div className="radio-options">
                {question.options.map((option) => (
                  <div key={option} className="radio-option">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name={question.id}
                        value={option}
                        checked={answers[question.id] === option}
                        onChange={() =>
                          handleAnswerChange(
                            question.id,
                            option,
                            question.questionType
                          )
                        }
                        className="radio-input"
                      />
                      <span className="option-text">{option}</span>
                    </label>
                  </div>
                ))}
              </div>
            )}

            {/* Multi Select */}
            {question.questionType === "multi_select" && (
              <div className="radio-options">
                {question.options.map((option) => (
                  <div key={option} className="radio-option">
                    <label className="radio-label">
                      <input
                        type="checkbox"
                        value={option}
                        checked={(answers[question.id] || []).includes(
                          option
                        )}
                        onChange={() =>
                          handleAnswerChange(
                            question.id,
                            option,
                            question.questionType
                          )
                        }
                        className="radio-input"
                      />
                      <span className="option-text">{option}</span>
                    </label>
                  </div>
                ))}
              </div>
            )}

            {/* True/False */}
            {question.questionType === "true_false" && (
              <div className="radio-options">
                {["True", "False"].map((option) => (
                  <div key={option} className="radio-option">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name={question.id}
                        value={option}
                        checked={answers[question.id] === option}
                        onChange={() =>
                          handleAnswerChange(
                            question.id,
                            option,
                            question.questionType
                          )
                        }
                        className="radio-input"
                      />
                      <span className="option-text">{option}</span>
                    </label>
                  </div>
                ))}
              </div>
            )}

            {/* Yes/No */}
            {question.questionType === "yes_no" && (
              <div className="radio-options">
                {["Yes", "No"].map((option) => (
                  <div key={option} className="radio-option">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name={question.id}
                        value={option}
                        checked={answers[question.id] === option}
                        onChange={() =>
                          handleAnswerChange(
                            question.id,
                            option,
                            question.questionType
                          )
                        }
                        className="radio-input"
                      />
                      <span className="option-text">{option}</span>
                    </label>
                  </div>
                ))}
              </div>
            )}

            {/* Free Text */}
            {question.questionType === "free_text" && (
              <textarea
                value={answers[question.id] || ""}
                onChange={(e) =>
                  handleAnswerChange(
                    question.id,
                    e.target.value,
                    question.questionType
                  )
                }
                placeholder="Type your answer..."
                rows={3}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  fontSize: "16px",
                  boxSizing: "border-box",
                }}
              />
            )}
          </div>
        ))}

        <button type="submit" className="button" disabled={submitting}>
          {submitting ? "Submitting..." : "Submit"}
        </button>
      </form>
    </div>
  );
};

export default QuestionnaireScreen;
